import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useI18n } from '../contexts/I18nContext';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { NotificationBell } from './NotificationBell';
import { StreakBadge } from './StreakBadge';
import { TutorialButton, HelpPointer } from './TutorialSystem';
import {
  Home,
  Import,
  BookMarked,
  RefreshCw,
  BarChart3,
  BarChart2,
  Settings,
  Moon,
  Sun,
  LogOut,
  Target,
  PanelLeftClose,
  PanelLeftOpen,
  Shield,
  Upload,
  FileText,
  Gamepad2,
  Puzzle,
  Database,
  Mail,
  Coins,
  BookOpen,
  Languages,
  Users,
  Zap,
  Plus,
  Flame,
  FlaskConical,
  Search,
  Menu,
  X,
  Globe,
  HelpCircle,
  Library,
  GraduationCap,
  ChevronDown,
  ChevronUp,
  ScrollText,
  Headphones,
  MessageCircle,
  MessageSquare,
  Youtube,
  Layers,
  FolderArchive,
  Brain,
  AlertTriangle,
  Terminal,
  Compass,
} from 'lucide-react';
import api from '../utils/api';

const LOGO_URL =
  'https://customer-assets.emergentagent.com/job_a7848912-da36-45ee-87b6-fb3028060d76/artifacts/ryg7545u_ChatGPT%20Image%20Mar%2012%2C%202026%2C%2003_29_06%20AM.png';

// Main navigation - grouped logically (using translation keys)
const coreNav = [
  { path: '/dashboard', labelKey: 'nav.home', icon: Home },
  { path: '/onboarding?revisit=true', labelKey: 'nav.learningPath', icon: Compass },
  { path: '/forge', labelKey: 'nav.forgePath', icon: Flame },
  { path: '/forge-stories', labelKey: 'nav.storyPath', icon: ScrollText },
];

const studyNav = [
  { path: '/library', labelKey: 'nav.library', icon: Library },
  { path: '/videos', labelKey: 'nav.videoLibrary', icon: Youtube },
  { path: '/import', labelKey: 'nav.import', icon: Import },
  { path: '/alphabet-courses?tab=modern', labelKey: 'nav.alphabets', icon: Languages },
  { path: '/grammar-library', labelKey: 'nav.grammarLibrary', icon: GraduationCap },
  { path: '/sentence-library', labelKey: 'nav.sentenceLibrary', icon: MessageSquare },
  { path: '/verb-formations', labelKey: 'nav.verbFormations', icon: Layers },
];

// Biblical Languages sub-navigation (collapsible)
const biblicalNav = [
  { path: '/bible-courses', labelKey: 'nav.bibleStudy', icon: BookOpen },
  { path: '/alphabet-courses?tab=biblical', labelKey: 'nav.biblicalAlphabets', icon: Languages },
  { path: '/grammar-library?lang=hbo', labelKey: 'nav.biblicalGrammar', icon: BookMarked },
];

const practiceNav = [
  { path: '/vocabulary', labelKey: 'nav.vocabulary', icon: Database },
  { path: '/review', labelKey: 'nav.review', icon: RefreshCw },
  { path: '/memory', labelKey: 'nav.memory', icon: Brain },
  { path: '/listening/es', labelKey: 'nav.listening', icon: Headphones },
  { path: '/conversation', labelKey: 'nav.conversation', icon: MessageCircle },
  { path: '/game', labelKey: 'nav.wordHunt', icon: Gamepad2 },
  { path: '/games', labelKey: 'nav.games', icon: Puzzle },
];

const socialNav = [
  { path: '/community', labelKey: 'nav.community', icon: Users },
  { path: '/stats', labelKey: 'nav.stats', icon: BarChart3 },
];

// Combine for mobile bar (first 5 items)
const mainNav = [...coreNav, ...studyNav, ...practiceNav, ...socialNav];

const goalNav = { path: '/goals', labelKey: 'nav.goals', icon: Target };

const EXPANDED_W = 280;
const COLLAPSED_W = 72;

// Language options for interface - ordered by global usage
const INTERFACE_LANGUAGES = [
  { code: 'en',    label: 'English',        flag: '🇺🇸', countryCode: 'us' },
  { code: 'pt-BR', label: 'Português (BR)', flag: '🇧🇷', countryCode: 'br' },
  { code: 'es',    label: 'Español',        flag: '🇪🇸', countryCode: 'es' },
  { code: 'fr',    label: 'Français',       flag: '🇫🇷', countryCode: 'fr' },
  { code: 'ru',    label: 'Русский',        flag: '🇷🇺', countryCode: 'ru' },
  { code: 'zh',    label: '中文',            flag: '🇨🇳', countryCode: 'cn' },
  { code: 'fa',    label: 'فارسی',          flag: '🇮🇷', countryCode: 'ir' },
  { code: 'ar',    label: 'العربية',        flag: '🇸🇦', countryCode: 'sa' },
];

// Flag component that uses images on desktop, emojis on mobile (fallback)
const FlagDisplay = ({ lang, size = "sm", showLabel = false }) => {
  const sizeClass = size === "sm" ? "w-5 h-4" : "w-6 h-5";
  
  return (
    <span className="inline-flex items-center gap-1.5">
      <img 
        src={`https://flagcdn.com/w40/${lang.countryCode}.png`}
        srcSet={`https://flagcdn.com/w80/${lang.countryCode}.png 2x`}
        alt={lang.label}
        className={`${sizeClass} object-cover rounded-sm`}
        loading="lazy"
        onError={(e) => {
          // Fallback to emoji if image fails
          e.target.style.display = 'none';
          e.target.nextSibling.style.display = 'inline';
        }}
      />
      <span className="emoji-fallback" style={{ display: 'none' }}>{lang.flag}</span>
      {showLabel && <span>{lang.label}</span>}
    </span>
  );
};

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme, halloweenMode, toggleHalloween } = useTheme();
  const { locale: language, setLocale: setLanguage, t, isRTL } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('iling-sidebar-collapsed');
    return saved === 'true';
  });
  const [credits, setCredits] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [biblicalExpanded, setBiblicalExpanded] = useState(() => {
    const saved = localStorage.getItem('iling-biblical-expanded');
    return saved !== 'false'; // Default to expanded
  });

  // Critical alert banner (admin only)
  const [criticalCount, setCriticalCount] = useState(0);
  const [alertBannerDismissed, setAlertBannerDismissed] = useState(false);

  useEffect(() => {
    if (!user?.is_admin) return;
    const fetchAlertCount = async () => {
      try {
        const res = await api.get('/logs/critical-alerts');
        setCriticalCount(res.data.unread_count || 0);
      } catch {}
    };
    fetchAlertCount();
    const interval = setInterval(fetchAlertCount, 60000); // re-check every minute
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    localStorage.setItem('iling-sidebar-collapsed', collapsed);
  }, [collapsed]);

  useEffect(() => {
    localStorage.setItem('iling-biblical-expanded', biblicalExpanded);
  }, [biblicalExpanded]);

  // Fetch user credits
  useEffect(() => {
    const fetchCredits = async () => {
      if (!user) return;
      try {
        const response = await api.get('/credits/balance');
        setCredits(response.data.balance || 0);
      } catch (err) {
        console.error('Failed to fetch credits:', err);
      }
    };
    fetchCredits();
  }, [user]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close mobile menu when navigating
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const isActive = (path) =>
    location.pathname === path ||
    (path !== '/dashboard' && location.pathname.startsWith(path));

  const currentLang = INTERFACE_LANGUAGES.find(l => l.code === language) || INTERFACE_LANGUAGES[0];

  // Handle language change
  const handleLanguageChange = (langCode) => {
    console.log('Changing language to:', langCode);
    setLanguage(langCode);
  };

  // Render nav section with optional label.
  // Labels visible when sidebar is expanded OR when on mobile (sidebar is full-width drawer).
  const showLabels = !collapsed || isMobile;
  const renderNavSection = (items, labelKey = null, labelColor = null) => (
    <>
      {labelKey && showLabels && (
        <div className="nav-section-label" style={{ color: labelColor || 'var(--muted-foreground)', marginTop: '12px', marginBottom: '4px' }}>
          {t(labelKey)}
        </div>
      )}
      {items.map((item) => {
        const Icon = item.icon;
        const active = isActive(item.path);
        const label = t(item.labelKey);
        return (
          <Link key={item.path} to={item.path} className="nav-item-link">
            <div
              className={`nav-item ${active ? 'active' : ''}`}
              data-testid={`nav-${item.labelKey.split('.')[1]}`}
              title={!showLabels ? label : undefined}
            >
              <span className="nav-icon"><Icon size={20} /></span>
              {showLabels && label}
            </div>
          </Link>
        );
      })}
    </>
  );

  return (
    <div className={`min-h-screen bg-background flex ${isRTL ? 'flex-row-reverse' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* ═══ Mobile Sidebar Overlay ═══ */}
      {isMobile && mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 backdrop-blur-sm"
          onClick={() => setMobileMenuOpen(false)}
          data-testid="mobile-overlay"
        />
      )}

      {/* ═══ Desktop Sidebar / Mobile Slide-out Menu ═══ */}
      <div
        className={`sidebar flex ${collapsed ? 'collapsed' : ''} ${isMobile ? 'mobile-sidebar' : ''} ${isMobile && mobileMenuOpen ? 'open' : ''} ${isRTL ? 'rtl-sidebar' : ''}`}
        style={{ 
          width: isMobile ? 280 : (collapsed ? COLLAPSED_W : EXPANDED_W),
          transform: isMobile && !mobileMenuOpen 
            ? (isRTL ? 'translateX(100%)' : 'translateX(-100%)') 
            : 'translateX(0)',
          [isRTL ? 'right' : 'left']: isMobile ? 0 : undefined,
        }}
        data-testid="sidebar"
      >
        {/* Close button for mobile */}
        {isMobile && (
          <button 
            className={`absolute top-4 ${isRTL ? 'left-4' : 'right-4'} p-2 text-slate-400 hover:text-white z-50`}
            onClick={() => setMobileMenuOpen(false)}
            data-testid="mobile-menu-close"
          >
            <X size={24} />
          </button>
        )}

        {/* Brand */}
        <div className="sidebar-brand">
          <div className="logo-shell">
            <img src={LOGO_URL} alt="iLing" className="sidebar-logo" />
          </div>
          {(!collapsed || isMobile) && <span className="brand-text">iLing</span>}
        </div>

        {/* Navigation - Grouped Sections */}
        <nav className="sidebar-nav" data-testid="sidebar-nav">
          {/* Core */}
          {renderNavSection(coreNav)}
          
          <div className="nav-divider" />
          
          {/* Study */}
          {renderNavSection(studyNav, showLabels ? 'nav.study' : null)}
          
          {/* Biblical Languages - Collapsible */}
          {(!collapsed || isMobile) ? (
            <div className="mt-2">
              <button
                onClick={() => setBiblicalExpanded(!biblicalExpanded)}
                className="w-full flex items-center justify-between px-3 py-2 text-sm text-amber-400/80 hover:text-amber-400 transition-colors"
                data-testid="biblical-section-toggle"
              >
                <div className="flex items-center gap-2">
                  <ScrollText size={16} />
                  <span>{t('nav.biblicalLanguages') || 'Biblical Languages'}</span>
                </div>
                {biblicalExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {biblicalExpanded && (
                <div className="pl-2 border-l-2 border-amber-500/30 ml-4 space-y-1">
                  {biblicalNav.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.path);
                    const label = t(item.labelKey) || item.labelKey.split('.').pop();
                    return (
                      <Link key={item.path} to={item.path} className="nav-item-link">
                        <div
                          className={`nav-item text-sm ${active ? 'active' : ''}`}
                          style={{ padding: '8px 12px' }}
                          data-testid={`nav-${item.labelKey.split('.')[1]}`}
                          title={label}
                        >
                          <span className="nav-icon"><Icon size={18} /></span>
                          {label}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            // Collapsed view - show icon only for Bible Study
            <Link to="/bible-courses" className="nav-item-link">
              <div
                className={`nav-item ${isActive('/bible-courses') ? 'active' : ''}`}
                title="Biblical Languages"
                data-testid="nav-biblical-collapsed"
              >
                <span className="nav-icon"><ScrollText size={20} /></span>
              </div>
            </Link>
          )}
          
          <div className="nav-divider" />
          
          {/* Practice */}
          {renderNavSection(practiceNav, showLabels ? 'nav.practice' : null)}
          
          <div className="nav-divider" />
          
          {/* Social */}
          {renderNavSection(socialNav, showLabels ? 'nav.social' : null)}

          <div className="nav-divider" />

          <Link to={goalNav.path} className="nav-item-link">
            <div
              className={`nav-item goal ${isActive(goalNav.path) ? 'active' : ''}`}
              data-testid="nav-goals"
              title={collapsed ? t(goalNav.labelKey) : undefined}
            >
              <span className="nav-icon"><goalNav.icon size={20} /></span>
              {(!collapsed || isMobile) && t(goalNav.labelKey)}
            </div>
          </Link>

          {user?.is_admin && (
            <>
              <div className="nav-divider" />
              {showLabels && (
                <div className="nav-section-label" style={{ color: 'var(--amber-400, #fbbf24)' }}>
                  {t('nav.admin')}
                </div>
              )}
              <Link to="/admin/dashboard" className="nav-item-link">
                <div
                  className={`nav-item ${isActive('/admin/dashboard') ? 'active' : ''}`}
                  data-testid="nav-admin-dashboard"
                  title={collapsed ? 'Dashboard' : undefined}
                  style={{ color: 'var(--amber-400, #fbbf24)' }}
                >
                  <span className="nav-icon"><BarChart2 size={20} /></span>
                  {(!collapsed || isMobile) && 'Dashboard'}
                </div>
              </Link>
              <Link to="/admin/users" className="nav-item-link">
                <div
                  className={`nav-item ${isActive('/admin/users') ? 'active' : ''}`}
                  data-testid="nav-admin-users"
                  title={collapsed ? t('nav.manageUsers') : undefined}
                  style={{ color: 'var(--amber-400, #fbbf24)' }}
                >
                  <span className="nav-icon"><Shield size={20} /></span>
                  {(!collapsed || isMobile) && t('nav.manageUsers')}
                </div>
              </Link>
              <Link to="/admin/texts" className="nav-item-link">
                <div
                  className={`nav-item ${isActive('/admin/texts') ? 'active' : ''}`}
                  data-testid="nav-admin-texts"
                  title={collapsed ? t('nav.manageTexts') : undefined}
                  style={{ color: 'var(--amber-400, #fbbf24)' }}
                >
                  <span className="nav-icon"><FileText size={20} /></span>
                  {(!collapsed || isMobile) && t('nav.manageTexts')}
                </div>
              </Link>
              <Link to="/admin/entries" className="nav-item-link">
                <div
                  className={`nav-item ${isActive('/admin/entries') ? 'active' : ''}`}
                  data-testid="nav-admin-entries"
                  title={collapsed ? t('nav.entryEdit') : undefined}
                  style={{ color: 'var(--amber-400, #fbbf24)' }}
                >
                  <span className="nav-icon"><Shield size={20} /></span>
                  {(!collapsed || isMobile) && t('nav.entryEdit')}
                </div>
              </Link>
              <Link to="/admin/batch-upload" className="nav-item-link">
                <div
                  className={`nav-item ${isActive('/admin/batch-upload') ? 'active' : ''}`}
                  data-testid="nav-admin-batch-upload"
                  title={collapsed ? t('nav.batchUpload') : undefined}
                  style={{ color: 'var(--amber-400, #fbbf24)' }}
                >
                  <span className="nav-icon"><Upload size={20} /></span>
                  {(!collapsed || isMobile) && t('nav.batchUpload')}
                </div>
              </Link>
              <Link to="/admin/frequencies" className="nav-item-link">
                <div
                  className={`nav-item ${isActive('/admin/frequencies') ? 'active' : ''}`}
                  data-testid="nav-admin-frequencies"
                  title={collapsed ? t('nav.freqLists') : undefined}
                  style={{ color: 'var(--amber-400, #fbbf24)' }}
                >
                  <span className="nav-icon"><Database size={20} /></span>
                  {(!collapsed || isMobile) && t('nav.freqLists')}
                </div>
              </Link>
              <Link to="/admin/flashcards" className="nav-item-link">
                <div
                  className={`nav-item ${isActive('/admin/flashcards') ? 'active' : ''}`}
                  data-testid="nav-admin-flashcards"
                  title={collapsed ? 'Flashcards' : undefined}
                  style={{ color: 'var(--amber-400, #fbbf24)' }}
                >
                  <span className="nav-icon"><Layers size={20} /></span>
                  {(!collapsed || isMobile) && 'Flashcards'}
                </div>
              </Link>
              <Link to="/admin/entry-audit" className="nav-item-link">
                <div
                  className={`nav-item ${isActive('/admin/entry-audit') ? 'active' : ''}`}
                  data-testid="nav-admin-entry-audit"
                  title={collapsed ? 'Entry Audit' : undefined}
                  style={{ color: 'var(--amber-400, #fbbf24)' }}
                >
                  <span className="nav-icon"><Search size={20} /></span>
                  {(!collapsed || isMobile) && 'Entry Audit'}
                </div>
              </Link>
            </>
          )}

          {/* iLing Lab Section - Available to all logged in users */}
          {user && (
            <>
              {(!collapsed || isMobile) && (
                <div className="nav-section-label" style={{ color: 'var(--purple-400, #c084fc)', marginTop: '16px' }}>
                  <FlaskConical size={14} style={{ marginRight: '6px' }} />
                  {t('nav.ilingLab')}
                </div>
              )}
              <Link to="/lab/sentences" className="nav-item-link">
                <div
                  className={`nav-item ${isActive('/lab/sentences') ? 'active' : ''}`}
                  data-testid="nav-lab-sentences"
                  title={collapsed ? t('nav.sentenceExplorer') : undefined}
                  style={{ color: 'var(--purple-400, #c084fc)' }}
                >
                  <span className="nav-icon"><Search size={20} /></span>
                  {(!collapsed || isMobile) && t('nav.sentenceExplorer')}
                </div>
              </Link>
              {user.is_admin && (
                <Link to="/admin/sentences" className="nav-item-link">
                  <div
                    className={`nav-item ${isActive('/admin/sentences') ? 'active' : ''}`}
                    data-testid="nav-admin-sentences"
                    title={collapsed ? t('nav.manageSentences') : undefined}
                    style={{ color: 'var(--purple-400, #c084fc)' }}
                  >
                    <span className="nav-icon"><FileText size={20} /></span>
                    {(!collapsed || isMobile) && t('nav.manageSentences')}
                  </div>
                </Link>
              )}
              {user.is_admin && (
                <Link to="/admin/grammar" className="nav-item-link">
                  <div
                    className={`nav-item ${isActive('/admin/grammar') ? 'active' : ''}`}
                    data-testid="nav-admin-grammar"
                    title={collapsed ? 'Grammar Editor' : undefined}
                    style={{ color: 'var(--purple-400, #c084fc)' }}
                  >
                    <span className="nav-icon"><GraduationCap size={20} /></span>
                    {(!collapsed || isMobile) && 'Grammar Editor'}
                  </div>
                </Link>
              )}
              <Link to="/admin/videos" className="nav-item-link">
                <div
                  className={`nav-item ${isActive('/admin/videos') ? 'active' : ''}`}
                  data-testid="nav-admin-videos"
                  title={collapsed ? 'Featured Videos' : undefined}
                  style={{ color: 'var(--red-400, #f87171)' }}
                >
                  <span className="nav-icon"><Youtube size={20} /></span>
                  {(!collapsed || isMobile) && 'Featured Videos'}
                </div>
              </Link>
            </>
          )}
        </nav>

        {/* Bottom utilities */}
        <div className="sidebar-bottom">
          {/* Notification Bell with Preview */}
          {user && (
            <div className={`nav-item justify-center ${collapsed && !isMobile ? '' : 'px-2'}`} title={t('nav.notifications')}>
              <NotificationBell />
              {(!collapsed || isMobile) && <span className="ml-2">{t('nav.notifications')}</span>}
            </div>
          )}

          {/* Help/Tutorial Button */}
          {user && (
            <TutorialButton className={`nav-item ${collapsed && !isMobile ? 'justify-center px-0' : ''}`} />
          )}

          {/* Collapse toggle - desktop only */}
          {!isMobile && (
            <button
              onClick={() => setCollapsed((p) => !p)}
              className="nav-item"
              data-testid="sidebar-collapse-toggle"
              title={collapsed ? t('nav.expand') : t('nav.collapse')}
            >
              <span className="nav-icon">
                {collapsed ? <PanelLeftOpen size={20} /> : <PanelLeftClose size={20} />}
              </span>
              {!collapsed && t('nav.collapse')}
            </button>
          )}

          {/* Theme + Halloween toggles */}
          <div className={`flex items-center gap-1 px-2 py-1 ${collapsed && !isMobile ? 'justify-center' : ''}`}>
            <button
              onClick={toggleTheme}
              className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
              title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
            </button>
            <button
              onClick={toggleHalloween}
              data-testid="halloween-toggle"
              title={halloweenMode ? 'Turn off Halloween theme' : 'Halloween theme 🎃'}
              className={`p-1 rounded-lg transition-colors text-base leading-none select-none ${
                halloweenMode ? 'halloween-pumpkin-btn' : 'opacity-35 hover:opacity-80'
              }`}
            >
              🎃
            </button>
          </div>

          {/* User panel */}
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="sidebar-user" data-testid="user-menu-trigger">
                  <Avatar className="user-avatar">
                    <AvatarImage src={user.picture} alt={user.name} />
                    <AvatarFallback className="text-xs">
                      {user.name?.charAt(0)?.toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  {(!collapsed || isMobile) && <span className="user-name">{user.name}</span>}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="right" align="end" className="w-52">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user.name}</p>
                  <p className="text-xs text-muted-foreground">{user.email}</p>
                  <div className="flex items-center gap-1.5 mt-1.5 text-xs">
                    <Coins className="h-3.5 w-3.5 text-amber-500" />
                    <span className="font-medium text-amber-600 dark:text-amber-400">
                      {(user.credits || 0).toLocaleString()} {t('nav.credits').toLowerCase()}
                    </span>
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/mailroom" className="cursor-pointer">
                    <Mail className="mr-2 h-4 w-4" /> {t('nav.mailRoom')}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/admin/vault" className="cursor-pointer">
                    <FolderArchive className="mr-2 h-4 w-4" /> Vault
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/settings" className="cursor-pointer">
                    <Settings className="mr-2 h-4 w-4" /> {t('common.settings')}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={async () => {
                    try {
                      await api.post('/onboarding/reset');
                      navigate('/onboarding');
                    } catch (err) {
                      console.error('Failed to reset onboarding:', err);
                    }
                  }}
                  className="cursor-pointer"
                >
                  <Languages className="mr-2 h-4 w-4" /> Add Languages
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={logout}
                  className="cursor-pointer text-destructive focus:text-destructive"
                  data-testid="logout-button"
                >
                  <LogOut className="mr-2 h-4 w-4" /> {t('common.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* ═══ Mobile Bottom Bar - Always Visible ═══ */}
      <nav
        className={`md:hidden fixed bottom-0 left-0 right-0 z-50 mobile-bar ${isRTL ? 'flex-row-reverse' : ''}`}
        data-testid="mobile-nav"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <div className={`flex items-center justify-around h-16 px-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
          {/* Menu Toggle Button */}
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="mobile-nav-link"
            data-testid="mobile-menu-toggle"
          >
            <Menu size={22} />
            <span>{t('nav.menu')}</span>
          </button>

          {/* Core navigation items */}
          {[...coreNav, practiceNav[0], socialNav[1]].slice(0, 4).map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            const label = t(item.labelKey);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`mobile-nav-link ${active ? 'active' : ''}`}
                data-testid={`mobile-nav-${item.labelKey.split('.')[1]}`}
              >
                <Icon size={22} />
                <span>{label}</span>
              </Link>
            );
          })}

          {/* Language Button */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button 
                className="mobile-nav-link"
                data-testid="mobile-language-btn"
              >
                <Globe size={44} className="text-amber-400" />
                <FlagDisplay lang={currentLang} size="sm" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align={isRTL ? 'start' : 'end'} side="top" className="w-48 mb-2">
              <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                {t('nav.interfaceLanguage')}
              </div>
              <DropdownMenuSeparator />
              {INTERFACE_LANGUAGES.map((lang) => (
                <DropdownMenuItem
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  className={`cursor-pointer ${language === lang.code ? 'bg-amber-500/10 text-amber-400' : ''}`}
                >
                  <span className={`${isRTL ? 'ml-2' : 'mr-2'} inline-flex items-center`}>
                    <FlagDisplay lang={lang} size="sm" />
                  </span>
                  {lang.label}
                  {language === lang.code && <span className={isRTL ? 'mr-auto' : 'ml-auto'}>✓</span>}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </nav>

      {/* ═══ Main Content ═══ */}
      <MainContent collapsed={collapsed} isMobile={isMobile} isRTL={isRTL}
        criticalAlertCount={criticalCount}
        onDismissAlertBanner={() => setAlertBannerDismissed(true)}
        alertBannerDismissed={alertBannerDismissed}
      >{children}</MainContent>
      
      {/* Help Pointer - shows on first login */}
      {user && <HelpPointer />}
    </div>
  );
};

export default Layout;

function MainContent({ collapsed, isMobile, isRTL, children, criticalAlertCount, onDismissAlertBanner, alertBannerDismissed }) {
  const { user } = useAuth();
  const { theme, toggleTheme, halloweenMode, toggleHalloween } = useTheme();
  const { pathname } = useLocation();
  const isDashboard = pathname.replace(/\/+$/, '') === '/dashboard';
  const isReader = pathname.startsWith('/reader/');
  const [readerNavHeight, setReaderNavHeight] = useState(0);
  const [isMd, setIsMd] = useState(window.innerWidth >= 768);
  const [streak, setStreak] = useState(null);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = (e) => setIsMd(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Fetch streak data
  useEffect(() => {
    if (!user || isDashboard) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/goals/streak');
        if (!cancelled) setStreak(res.data);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [user, isDashboard]);

  const marginStyle = isMd 
    ? { [isRTL ? 'marginRight' : 'marginLeft']: collapsed ? COLLAPSED_W : EXPANDED_W }
    : {};

  useEffect(() => {
    if (!isReader) return;
    const nav = document.querySelector('[data-testid="mobile-nav"]');
    if (!nav) return;
    const update = () => setReaderNavHeight(nav.getBoundingClientRect().height);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(nav);
    window.addEventListener('resize', update);
    return () => { observer.disconnect(); window.removeEventListener('resize', update); };
  }, [isReader]);

  return (
    <main
      className={isReader ? 'flex-1 min-w-0 min-h-0 h-[100dvh] flex flex-col overflow-hidden' : 'flex-1 min-w-0 transition-all duration-200 pb-20 md:pb-0 min-h-screen'}
      style={{ ...marginStyle, ...(isReader ? { paddingBottom: readerNavHeight } : {}) }}
    >
      {/* Critical alert banner — shown to admins when unread alerts exist */}
      {user?.is_admin && criticalAlertCount > 0 && !alertBannerDismissed && !isReader && (
        <div className="shrink-0 flex items-center justify-between gap-3 px-4 py-2 bg-red-950/80 border-b border-red-500/40 text-xs"
          data-testid="critical-alert-banner">
          <div className="flex items-center gap-2 text-red-200">
            <AlertTriangle size={14} className="text-red-400 shrink-0" />
            <span>
              <span className="font-bold text-red-300">{criticalAlertCount} critical alert{criticalAlertCount !== 1 ? 's' : ''}</span>
              {' '}detected in production.
            </span>
            <Link to="/admin/logs" className="underline text-red-300 hover:text-red-100 font-medium">
              View logs
            </Link>
          </div>
          <button onClick={onDismissAlertBanner} className="text-red-500 hover:text-red-300 p-0.5"
            data-testid="dismiss-alert-banner" title="Dismiss for this session">
            <X size={14} />
          </button>
        </div>
      )}

      {/* Top utility bar */}
      {user && !isDashboard && (
        <div className="flex shrink-0 items-center justify-end gap-2 px-4 md:px-6 pt-3 pb-1" data-testid="top-bar">
          {/* Streak counter */}
          <StreakBadge streak={streak} />

          {/* Dark mode toggle */}
          <button
            onClick={toggleTheme}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
            data-testid="top-theme-toggle"
            title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
          >
            {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
          </button>

          {/* Halloween seasonal toggle */}
          <button
            onClick={toggleHalloween}
            title={halloweenMode ? 'Turn off Halloween theme' : 'Halloween theme'}
            data-testid="halloween-toggle"
            className={`p-1 rounded-lg transition-colors text-base leading-none select-none ${
              halloweenMode
                ? 'halloween-pumpkin-btn opacity-100'
                : 'opacity-40 hover:opacity-80'
            }`}
          >
            🎃
          </button>

          {/* Settings */}
          <Link
            to="/settings"
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
            data-testid="top-settings-link"
            title="Settings"
          >
            <Settings size={16} />
          </Link>
        </div>
      )}

      <div className={isReader ? 'px-2 md:px-4 pb-2 max-w-[1400px] mx-auto w-full min-w-0 flex-1 min-h-0' : `px-4 md:px-6 pb-4 md:pb-6 max-w-[1400px] mx-auto min-w-0 ${isDashboard ? 'pt-3' : ''}`}>{children}</div>
    </main>
  );
}
